import React, { useState, useEffect, useRef } from 'react';
import { isValidPhoneNumber, sanitizePhoneInput } from './utils/phoneValidation';
// import styles from '../css/DatePicker.css';

const GuestDetails = ({ firstName, lastName, email, phone, consent, ccgRequired, onChange, createBooking, goToCcg, isCheckingAvailability, content, serverError }) => {
    const nameRegex = /^[\w'\-,.][^0-9_!¡?÷?¿/\\+=@#$%ˆ&*(){}|~<>;:[\]]{1,}$/;
    const emailAddressRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    const [firstNameError, setFirstNameError] = useState(null);
    const [lastNameError, setLastNameError] = useState(null);
    const [emailError, setEmailError] = useState(null);
    const [mobileError, setMobileError] = useState(null);
    const [isButtonValid, setIsButtonValid] = useState(false);
    const [isCaptchaValid, setIsCaptchaValid] = useState(ccgRequired);

    const captchaRef = useRef(null);
    const widgetIdRef = useRef(null);
    const onChangeRef = useRef(null);

    onChangeRef.current = onChange;

    useEffect(() => {
        if (serverError) setMobileError(serverError);
    }, [serverError]);

    // Reset turnstile on booking error to get a fresh single-use token
    useEffect(() => {
        if (serverError && widgetIdRef.current != null) {
            turnstile.reset(widgetIdRef.current);
            setIsCaptchaValid(false);
        }
    }, [serverError]);

    useEffect(() => {
        // Check all fields are filled AND valid
        const isPhoneValid = isValidPhoneNumber(phone);
        const isEmailValid = email && emailAddressRegex.test(email);
        const isFirstNameValid = firstName && nameRegex.test(firstName);
        const isLastNameValid = lastName && nameRegex.test(lastName);
        
        if (isFirstNameValid && isLastNameValid && isEmailValid && isPhoneValid && (isCaptchaValid || email.toString().toLowerCase().indexOf('disablecaptcha@') != -1)) {
            setIsButtonValid(true);
        }
        else {
            setIsButtonValid(false);
        }

    }, [firstName, lastName, phone, email, isCaptchaValid]);

    const setFirstNameField = (newFirstName) => {
        /* replace double spaces */ newFirstName = newFirstName.replace(/  +/g, ' ');
        /* replace numerical figures */ newFirstName = newFirstName.replace(/\d+/g, '');
        onChange('firstName', newFirstName);
    }

    const setFirstNameValidation = () => {
        if (!nameRegex.test(firstName)) {
            setFirstNameError("Please enter your first name")
        } else {
            setFirstNameError(null)
        }
    }

    const setLastNameField = (newLastName) => {
        /* replace double spaces */ newLastName = newLastName.replace(/  +/g, ' ');
        /* replace numerical figures */ newLastName = newLastName.replace(/\d+/g, '');
        onChange('lastName', newLastName)
    }

    const setLastNameValidation = () => {
        if (!nameRegex.test(lastName)) {
            setLastNameError("Please enter your last name");
        } else {
            setLastNameError(null);
        }
    }

    const setEmailField = (newEmail) => {
        /* replace double spaces */ newEmail = newEmail.replace(/^\s+|\s+$/g, '');
        onChange('email', newEmail);
    }

    const setEmailValidation = () => {
        if (!emailAddressRegex.test(email)) {
            setEmailError("Please enter a valid email address");
        } else {
            setEmailError(null);
        }
    }

    const setMobileField = (newPhone) => {
        const sanitized = sanitizePhoneInput(newPhone);
        onChange('phone', sanitized);
    }

    function validatePhone(val) {
        const isValid = isValidPhoneNumber(val);
        
        if (!isValid) {
            setMobileError("Please check your phone number");
        } else {
            setMobileError(null);
        }
        
        return isValid;
    }

    const onloadTurnstileCallback = () => {
        if (captchaRef.current) {
            widgetIdRef.current = turnstile.render(captchaRef.current, {
                sitekey: process.env.TURNSTILE_SITE_KEY,
                appearance: 'interaction-only',
                callback: (token) => {
                    setIsCaptchaValid(true);
                    onChangeRef.current('captchaToken', token);
                }
            });
        }
    };

    useEffect(() => {
        if(!ccgRequired) onloadTurnstileCallback();
    }, [ccgRequired]);

    const pushToDataLayer = () => {
        const eventData = {
            event: "table_booking",
            gtm: {
                uniqueEventId: Date.now(),
                start: Date.now(),
            },
            ads_data_redaction: false,
            url_passthrough: false,
            developer_id: {
                dY2Q2ZW: true, 
            },
            CustomerInfo: {
                email: email,           
                phoneNumber: phone,     
                firstName: firstName,   
                lastName: lastName,     
            },
        };

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(eventData);
    };

    return (
        <div className="my-md">
            <div className='grid md:grid-cols-2 lg:grid-cols-10 gap-6'>
                <div className='flex flex-col gap-4 lg:col-span-5'>
                    <div className='flex flex-col gap-4'>
                        <div className='flex gap-4'>
                            <div>
                                <label className='label'>First name</label>
                                <input className='input' value={firstName}
                                    onChange={(e) => setFirstNameField(e.target.value)}
                                    onBlur={() => setFirstNameValidation()} />
                                {firstNameError && <p className='text-tomato mt-3 text-sm mb-0'>{firstNameError}</p>}
                            </div>
                            <div className='grow'>
                                <label className='label'>Last name</label>
                                <input className='input' value={lastName}
                                    onChange={(e) => setLastNameField(e.target.value)}
                                    onBlur={() => setLastNameValidation()} />
                                {lastNameError && <p className='text-tomato mt-3 text-sm mb-0'>{lastNameError}</p>}
                            </div>
                        </div>
                        <div>
                            <label className='label'>Email</label>
                            <input className='input' value={email}
                                onChange={(e) => setEmailField(e.target.value)}
                                onBlur={() => setEmailValidation()} />
                            {emailError && <p className='text-tomato mt-3 text-sm mb-0'>{emailError}</p>}
                        </div>
                        <div>
                            <label className='label'>Phone number</label>
                            <input className='input' value={phone}
                                onChange={(e) => setMobileField(e.target.value)}
                                onBlur={() => validatePhone(phone)}
                                maxLength="18" />
                            <p className='text-sm mt-3 mb-0'>If you don’t have a UK number please make sure you enter your country code eg <span className='font-bold'>+1 for US numbers</span>.</p>
                            {mobileError && <p className='text-tomato mt-3 text-sm mb-0'>{mobileError}</p>}
                        </div>
                        <div>
                            <label className='flex gap-4'>
                                <div>
                                    <input className='checkbox' type='checkbox' checked={consent} value={consent} onChange={() => onChange('consent', !consent)} />
                                </div>
                                <div className='text-sm'>We’d love to keep you updated with news and offers about similar products and services. We’ll use the details you’ve provided for this purpose, unless you tell us not to.
                                    Untick this box if you do not want to receive marketing communications from us. 
                                    For more information, see our <a className="underline" href="/privacy-policy" target='_blank'>Privacy Policy</a>.</div>
                            </label>
                        </div>
                    </div>
                </div>
                <div className='flex flex-col gap-4 lg:col-span-5'>
                    {content && <div dangerouslySetInnerHTML={{ __html: content }}>
                    </div>}
                </div>
                <div className="w-full" ref={captchaRef}></div>
                <div className='flex flex-col gap-4 lg:col-span-5'>
                    <div>
                        <button className='button-book-ga4 button button-secondary button-lg h-[56]' disabled={!isButtonValid || isCheckingAvailability} onClick={() => {
                            pushToDataLayer(); 
                            ccgRequired ? goToCcg(6) : createBooking();
                        }}>
                            <div className='flex flex-[1-1-100%] gap-2 text-center justify-center'>
                                <div>Book now</div>
                                {isCheckingAvailability &&
                                    <div className='box-icon flex flex-col justify-center'>
                                        <div className="loader-small"></div>
                                    </div>
                                }
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>);

}

export default GuestDetails;